import { Component, ViewChild } from '@angular/core';
import {
  ActionButton,
  ButtonColumn,
  DATA_SERVICE_TOKEN,
  EoSearchPageComponent,
  STATUS,
  SearchPageOptionsService,
} from 'ngx-eo-web-components';
import { ConfirmationService, LazyLoadEvent } from 'primeng/api';
import { BehaviorSubject, firstValueFrom, tap } from 'rxjs';
import { UserFormDialogComponent } from '../user-form-dialog/user-form-dialog.component';
import { userFormConfig } from '../../config/user-form.config';
import { userSearchFormConfig } from '../../config/user-search-form.config';
import { UserService } from '../../../../services/user/user.service';
import { UsersTableConfigBuilder } from '../../config/user-search.config';
import { User } from '../../../core/models/user.model';
import { AuthService } from '../../../../services/auth/auth.service';
import { CoreService } from '../../../core/core.service';
import { ISortParams } from '../../../shared/models/sort.model';

@Component({
  selector: 'app-user-search',
  templateUrl: './user-search.component.html',
  styleUrl: './user-search.component.scss',
  providers: [
    SearchPageOptionsService,
    { provide: DATA_SERVICE_TOKEN, useClass: UserService },
  ],
})
export class UserSearchComponent extends EoSearchPageComponent<User> {
  @ViewChild('userFormDialog', { static: false })
  userFormDialog?: UserFormDialogComponent;

  data$ = new BehaviorSubject<User[]>([]);
  override tableConfig = this.getUserTableConfig();

  protected getUserTableConfig() {
    let data = this.searchService.searchCh.data$.getValue()?.resultset ?? [];
    data = this.mapIconClasses(data);
    return new UsersTableConfigBuilder()
      .addFunction('onEditUser', this.onEditUser.bind(this))
      .addFunction('onCreateUser', this.onCreateUser.bind(this))
      .addFunction(
        'getEmailConfirmedToolTip',
        this.getEmailConfirmedToolTip.bind(this)
      )
      .addFunction(
        'getLockoutEnabledToolTip',
        this.getLockoutEnabledToolTip.bind(this)
      )
      .addIsResetPasswordVisibleFunction(this.isResetPasswordVisible.bind(this))
      .addIsConfirmEmailVisibleFunction(this.isConfirmEmailVisible.bind(this))
      .addResetPasswordFunction(this.resetPassword.bind(this))
      .addConfirmEmailFunction(this.confirmEmail.bind(this))
      .getTable(data);
  }

  override defaultSearchParam = {
    QueryTerm: '',
    Role: '',
    SortParams: null,
  };

  override searchParam = { ...this.defaultSearchParam };
  override formConfig = userSearchFormConfig;

  constructor(
    override searchService: UserService,
    private readonly authService: AuthService,
    private readonly coreService: CoreService
  ) {
    super(searchService);
  }

  private mapIconClasses(data: User[]) {
    const checkIconClass = 'check-circle p-success';
    const timesIconClass = 'times-circle p-error';

    const unLockedIconClass = 'unlock p-success';
    const lockedIconClass = 'lock p-error';

    return data.map((x) => ({
      ...x,
      lockoutEnabledIcon: x.lockoutEnabled
        ? lockedIconClass
        : unLockedIconClass,
      emailConfirmedIcon: x.emailConfirmed ? checkIconClass : timesIconClass,
    }));
  }

  public getEmailConfirmedToolTip(item: User) {
    return item?.emailConfirmed ? 'ΝΑΙ' : 'ΟΧΙ';
  }

  public getLockoutEnabledToolTip(item: User) {
    return item?.lockoutEnabled ? 'ΝΑΙ' : 'ΟΧΙ';
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.searchService.searchCh.data$.subscribe(() => {
      if (this.tableConfig) {
        this.tableConfig = this.getUserTableConfig();
      }
    });
  }

  onEditUser(item: any) {
    this.userFormDialog?.show(item);
  }

  onCreateUser() {
    const item = new User();
    this.userFormDialog?.show();
  }

  onUserFormSuccess() {
    this.onSearch();
  }

  onResetPasswordSuccess() {
    this.coreService.showSuccess(
      'Το Email για επαναφορά κωδικού αποστάλθηκε με επιτυχία στον χρήστη.'
    );
  }

  onConfirmEmailSuccess() {
    this.coreService.showSuccess(
      'Το μήνυμα για επιβεβαίωση email αποστάλθηκε με επιτυχία στον χρήστη.'
    );
  }

  onSearch(searchParams?: any) {
    if (searchParams) this.searchParam = searchParams?.form;

    this.searchService
      .searchUsers(this.searchParam, this.getPagesize(), this.getPage())
      .subscribe();
  }

  override isLoading(): boolean {
    return (
      this.searchService.searchCh.status$.getValue() === STATUS.loading ||
      this.authService.userResetPasswordCallHandler.status$.getValue() ===
        STATUS.loading ||
      this.authService.userEmailConfirmCallHandler.status$.getValue() ===
        STATUS.loading
    );
  }

  private isResetPasswordVisible(user?: User): boolean {
    return this.authService.hasRoles(['Admin']);
  }

  private isConfirmEmailVisible(user?: User): boolean {
    return this.authService.hasRoles(['Admin']) && !user?.emailConfirmed;
  }

  private resetPassword(user?: User, event?: any) {
    if (user == null || user == undefined) {
      return;
    }
    this.confirmationService.confirm({
      target: event.target,
      header: 'ΠΡΟΣΟΧΗ!',
      message: `Πρόκειται να αποστείλετε Email για επαναφορά κωδικού στον χρήστη ${user.userName}. Θέλετε να προχωρήσετε;`,
      rejectLabel: 'Όχι',
      closeOnEscape: false,
      icon: 'pi pi-exclamation-triangle p-warning',
      acceptLabel: 'Ναι',
      rejectVisible: true,
      rejectButtonStyleClass: 'p-button-text',
      key: 'approveResetPopup',
      accept: () => {
        return this.authService
          .requestResetPassword(user.userID ?? '')
          .subscribe(() => this.onResetPasswordSuccess());
      },
    });
  }

  private confirmEmail(user?: User, event?: any) {
    if (user == null || user == undefined) {
      return;
    }
    this.confirmationService.confirm({
      target: event.target,
      header: 'ΠΡΟΣΟΧΗ!',
      message: `Πρόκειται να αποστείλετε μήνυμα για επιβεβαίωση email στον χρήστη ${user.userName}. Θέλετε να προχωρήσετε;`,
      rejectLabel: 'Όχι',
      closeOnEscape: false,
      icon: 'pi pi-exclamation-triangle p-warning',
      acceptLabel: 'Ναι',
      rejectVisible: true,
      rejectButtonStyleClass: 'p-button-text',
      key: 'approveResetPopup',
      accept: () => {
        return this.authService
          .requestConfirmEmail(user.userID ?? '')
          .subscribe(() => this.onConfirmEmailSuccess());
      },
    });
  }

  override getTotalCount(): number {
    return this.searchService.searchCh.data$.getValue()?.totalCount ?? 0;
  }
}
